import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "src/components/app/layout/Header";
import InsightsChart from "src/components/app/insights/InsightsChart";
import LogHistory from "src/components/app/log-details/LogHistory";
import LogTrends from "src/components/app/log-details/LogTrends";
import InsightsPopover from "src/components/app/InsightsPopover";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as insightsActions from "src/redux/slices/insightsSlice";
import * as reminderActions from "src/redux/slices/reminder";
import moment from "moment";
import Dropdown from "src/components/common/Dropdown";
import Tabs from "src/components/app/Tabs";
import { SCREEN_NAMES } from "src/helpers/analytics/events";
import analytics from "src/helpers/analytics";

const macronutrientsOptions = [
  { value: "cals", label: "Calories(kcal)", unit: "kcal" },
  // { value: "carbs", label: "Carbs(g)", unit: "g" },
  { value: "proteins", label: "Proteins(g)", unit: "g" },
  { value: "fats", label: "Fats(g)", unit: "g" },
];

const DEFAULT_NUTRITION_TYPE = "cals";

const hours = [
  "00:00",
  "01:00",
  "02:00",
  "03:00",
  "04:00",
  "05:00",
  "06:00",
  "07:00",
  "08:00",
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
  "18:00",
  "19:00",
  "20:00",
  "21:00",
  "22:00",
  "23:00",
];

//const weekDays = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const monthDays = Array.from({ length: 31 }, (_, i) => i + 1);

const LogDetailsScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState("trends");
  const queryParams = new URLSearchParams(location.search);
  const logDetailsScreenKey = queryParams.get("log");
  const { patient } = useAppSelector((state) => state.auth);
  const { insightLog } = useAppSelector((state) => state.insights);
  const [activeFilter, setActiveFilter] = useState<string>("day");
  const [nutritionType, setNutritionType] = useState<string | number>(
    DEFAULT_NUTRITION_TYPE,
  );
  const [data, setData] = useState<any[]>([]);

  const screenTabs = [
    {
      id: "trends",
      title: "Trends",
    },
    {
      id: "history",
      title: "History",
    },
  ];

  const ChartTypes = [
    {
      name: "D",
      value: "day",
    },
    {
      name: "W",
      value: "week",
    },
    {
      name: "M",
      value: "month",
    },
    // {
    //   name: "Y",
    //   value: "year",
    // },
  ];

  useEffect(() => {
    // Event: LOG_DETAILS_SCREEN
    analytics.pageView(
      `${logDetailsScreenKey?.replace("-", "_")}_${
        SCREEN_NAMES.LOG_DETAILS_SCREEN
      }`,
    );
  }, []);

  // const handleTabChange = useCallback(
  //   (tabId: string) => {
  //     setActiveTab(tabId);
  //   },
  //   [activeTab]
  // );

  const dynamicHeader = (log: string) => {
    switch (log) {
      case "water-logs":
        return "Water Logs";
      case "sleep-logs":
        return "Sleep Logs";
      case "steps-logs":
        return "Steps Logs";
      case "diet-logs":
        return "Diet Logs";
      case "exercise-logs":
        return "Exercise Logs";
      case "fatigue-logs":
        return "Fatigue Logs";
      default:
        return "Log Details";
    }
  };

  const logType = (log: string) => {
    switch (log) {
      case "diet-logs":
        return "diet";
      case "water-logs":
        return "water";
      case "sleep-logs":
        return "sleep";
      case "steps-logs":
        return "steps";
      case "exercise-logs":
        return "exercise";
      case "fatigue-logs":
        return "fatigue";
    }
    return "diet";
  };

  // FETCH LOGGER API
  const fetchLogData = useCallback(async () => {
    new Promise((resolve, reject) => {
      const params = {
        // user_id: "8495000401", //patient.data?.contact_no,
        user_id: patient?.data?.patient_id,
        filter_type: activeFilter,
        logger: logType(logDetailsScreenKey ?? "Log Details"),
      };
      dispatch(
        insightsActions.getLogDataRequest({
          queryParams: params,
          resolve,
          reject,
        }),
      );
    });
  }, [dispatch, activeFilter]);

  useEffect(() => {
    fetchLogData();
  }, [fetchLogData]);

  // Fetch reminders when component mounts
  useEffect(() => {
    if (patient?.data?.patient_id) {
      new Promise((resolve, reject) => {
        dispatch(
          reminderActions.getReminderRequest({
            resolve,
            reject,
            payload: {
              user_id: patient?.data?.patient_id,
            },
          }),
        );
      }).catch((error) => {
        console.error("Error fetching reminders:", error);
      });
    }
  }, [dispatch, patient?.data?.patient_id]);

  useEffect(() => {
    if (!insightLog.data?.graph_data) return;

    if (activeFilter === "day") {
      const groupedByHour = hours.map((hour) => {
        const hourValue = parseInt(hour.split(":")[0], 10);

        // items belonging to this hour
        const itemsInHour = insightLog.data.graph_data.filter((item: any) => {
          const m = item?.timestamp || item?.time;
          if (!m) return false;
          return moment.utc(m).local().hour() === hourValue;
        });

        // total value
        const totalValue = itemsInHour.reduce(
          (acc: number, item: any) =>
            acc + (item?.[nutritionType as string] || item?.value || 0),
          0,
        );

        // label as "1PM to 2PM"
        const startLabel = moment(hour, "HH:mm").format("hA");
        const endLabel = moment(hour, "HH:mm").add(1, "hour").format("hA");
        const label = `${startLabel} to ${endLabel}`;

        return {
          time: startLabel, // for X-axis tick
          label, // e.g. "1PM to 2PM"
          value: totalValue,
        };
      });

      setData(groupedByHour);
    } else if (activeFilter === "week") {
      const endOfRange = moment(); // today (e.g. 18 Aug)
      const startOfRange = endOfRange.clone().subtract(6, "days"); // 7-day window

      const weekData = Array.from({ length: 7 }).map((_, idx) => {
        const dayMoment = startOfRange.clone().add(idx, "days");
        const label = dayMoment.format("D MMM"); // e.g., "12 Aug", "13 Aug", ...

        const item = insightLog.data.graph_data.find((item: any) => {
          const m = item?.time || item?.timestamp;
          return m && moment.utc(m).local().isSame(dayMoment, "day");
        });

        return {
          ...item,
          time: label,
          label: label,
          value: item?.value || 0,
        };
      });

      setData(weekData);
    } else if (activeFilter === "month") {
      const currentMonth = moment().month() + 1; // 1-based
      const currentYear = moment().year();

      const monthData = monthDays.map((day) => {
        const paddedDay = String(day).padStart(2, "0");

        // Try to find matching item
        const item = insightLog.data.graph_data.find((item: any) => {
          const m = item?.time || item?.timestamp;
          return m && moment.utc(m).local().format("DD") === paddedDay;
        });

        // Build YYYY-MM-DD safely
        const dateStr = `${currentYear}-${String(currentMonth).padStart(
          2,
          "0",
        )}-${paddedDay}`;
        const formattedDate = moment(dateStr, "YYYY-MM-DD", true).isValid()
          ? dateStr
          : null;

        return {
          ...item,
          time: formattedDate,
          label: formattedDate,
          value: item?.value || 0,
        };
      });

      setData(monthData);
    }
  }, [insightLog.data?.graph_data, nutritionType, activeFilter]);

  return (
    <div className="flex flex-col gap-4">
      <div className="relative h-[calc(100vh-75px)]">
        <div className="fixed top-0 left-0 right-0 z-10 max-w-lg mx-auto">
          <Header
            titlePosition="center"
            title={
              dynamicHeader(logDetailsScreenKey ?? "Log Details") ??
              "Log Details"
            }
            onBackClick={() => navigate(-1)}
            rightComponent={
              <InsightsPopover type={logDetailsScreenKey || ""} />
            }
          />
          <div className="p-4 space-y-4 bg-white border-b border-gray-200">
            <Tabs
              tabs={screenTabs}
              activeTab={activeTab}
              onChange={(tab) => setActiveTab(tab.id)}
            />
          </div>
        </div>
        <div className="w-full mt-32">
          {insightLog.data?.graph_data && activeTab === "trends" ? (
            <LogTrends
              // className="h-56"
              logDetailsScreenKey={logDetailsScreenKey}
              chartTitle={
                dynamicHeader(logDetailsScreenKey ?? "Log Details") ??
                "Log Details"
              }
              chartSection={
                <InsightsChart
                  // data={timeFormatedData}
                  data={data}
                  goal={
                    logDetailsScreenKey !== "diet-logs" &&
                    insightLog.data?.goal_value
                  }
                  xAxisKey="time"
                  barKey={
                    logDetailsScreenKey === "diet-logs" &&
                    activeFilter !== "day"
                      ? (nutritionType as string)
                      : "value"
                  }
                  unit={
                    logDetailsScreenKey === "diet-logs"
                      ? (macronutrientsOptions.find(
                          (option) => option.value === nutritionType,
                        )?.unit as string)
                      : insightLog.data?.unit || ""
                  }
                  showXAxis
                  // xAxisRanges={xAxisRanges}
                  height={250}
                />
              }
              isFilters={true}
              filterSection={ChartTypes.map((type, index) => (
                <button
                  key={index}
                  type="button"
                  className={`px-2.5 py-1 text-sm font-normal rounded-md transition-colors text-black ${
                    activeFilter === type.value
                      ? "bg-[var(--app-primary)] text-white"
                      : ""
                  }`}
                  onClick={() => {
                    setActiveFilter(type.value);
                    setNutritionType(DEFAULT_NUTRITION_TYPE);
                  }}
                >
                  {type.name}
                </button>
              ))}
              selectionSection={
                logDetailsScreenKey === "diet-logs" && (
                  <div className="w-full flex-1">
                    <Dropdown
                      name="nutritionType"
                      options={macronutrientsOptions}
                      value={nutritionType}
                      onChange={(value) => setNutritionType(value)}
                      fullWidth
                    />
                  </div>
                )
              }
            />
          ) : activeTab === "history" ? (
            <LogHistory logDetailsScreenKey={logDetailsScreenKey || ""} />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default LogDetailsScreen;
